import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { api, money, formatApiError } from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_COLORS = { paid: "bg-emerald-600", pending: "bg-amber-600", failed: "bg-red-600", refunded: "bg-zinc-600" };

export default function AdminOrders() {
  const { user } = useAdminAuth();
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [refundAmt, setRefundAmt] = useState("");

  const load = () => api.get(`/admin/orders${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openDetail = async (id) => { const { data } = await api.get(`/admin/orders/${id}`); setDetail(data); setRefundAmt(""); };

  const doRefund = async (full) => {
    if (!window.confirm(full ? "Issue a FULL refund? Tickets will be cancelled." : `Refund ${money(refundAmt)}?`)) return;
    try {
      await api.post(`/admin/orders/${detail.id}/refund`, { amount: full ? null : parseFloat(refundAmt), reason: "Admin refund" });
      toast.success("Refund confirmed");
      setDetail(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <h1 className="font-display text-3xl uppercase text-white mb-6">Orders</h1>
      <div className="relative mb-4 max-w-md">
        <Search size={16} className="absolute left-3 top-3 text-zinc-500" />
        <input data-testid="order-search" placeholder="Search order #, name, email…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="w-full rounded-md bg-[#141417] border border-[#27272A] pl-9 pr-4 py-2.5 text-white text-sm outline-none focus:border-[#D4AF37]" />
      </div>

      <div className="rounded-xl border border-[#27272A] overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-[#18181B] text-zinc-400"><tr>
            <th className="text-left px-4 py-3 font-medium">Order</th>
            <th className="text-left px-4 py-3 font-medium">Customer</th>
            <th className="text-left px-4 py-3 font-medium">Event</th>
            <th className="text-center px-4 py-3 font-medium">Tix</th>
            <th className="text-right px-4 py-3 font-medium">Total</th>
            <th className="text-center px-4 py-3 font-medium">Status</th>
          </tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.id}`} onClick={() => openDetail(o.id)} className="border-t border-[#27272A] text-zinc-200 cursor-pointer hover:bg-[#18181B]">
                <td className="px-4 py-3 font-mono text-xs">{o.order_number}</td>
                <td className="px-4 py-3">{o.customer}</td>
                <td className="px-4 py-3">{o.event}</td>
                <td className="px-4 py-3 text-center">{o.tickets}</td>
                <td className="px-4 py-3 text-right text-[#D4AF37]">{money(o.total)}</td>
                <td className="px-4 py-3 text-center"><span className={`text-[10px] font-oswald tracking-widest px-2 py-1 rounded text-white ${STATUS_COLORS[o.refund_status ? "refunded" : o.payment_status] || "bg-zinc-700"}`}>{(o.refund_status || o.payment_status).toUpperCase()}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {orders.length === 0 && <div className="p-10 text-center text-zinc-500">No orders found.</div>}
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-[#121215] border-[#27272A] text-white max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Order {detail.order_number}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div>
                  <p className="text-zinc-400 text-xs mb-1">CUSTOMER</p>
                  <p className="text-white">{detail.customer.first_name} {detail.customer.last_name}</p>
                  <p className="text-zinc-400">{detail.customer.email} · {detail.customer.phone}</p>
                </div>
                <div>
                  <p className="text-zinc-400 text-xs mb-1">TICKETS ({detail.tickets.length})</p>
                  {detail.tickets.map((t) => (
                    <div key={t.id} className="flex justify-between border-b border-[#27272A] py-1.5">
                      <span className="font-mono text-xs">{t.ticket_number} · {t.ticket_type_name}</span>
                      <span className={t.checked_in ? "text-emerald-400" : t.status === "cancelled" ? "text-red-400" : "text-zinc-500"}>{t.status === "cancelled" ? "Cancelled" : t.checked_in ? "✓ Checked in" : "Valid"}</span>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg bg-[#18181B] p-3 space-y-1">
                  <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>{money(detail.subtotal)}</span></div>
                  <div className="flex justify-between text-zinc-400"><span>Fees</span><span>{money(detail.fees)}</span></div>
                  {detail.tax > 0 && <div className="flex justify-between text-zinc-400"><span>Tax</span><span>{money(detail.tax)}</span></div>}
                  <div className="flex justify-between text-white font-bold"><span>Total</span><span className="text-[#D4AF37]">{money(detail.total)}</span></div>
                </div>
                <div className="flex justify-between"><span className="text-zinc-400">Payment</span><span className="uppercase font-oswald tracking-widest text-xs">{detail.refund_status || detail.payment_status}</span></div>

                {user.role === "OWNER" && detail.payment_status === "paid" && !detail.refund_status && (
                  <div className="border-t border-[#27272A] pt-4">
                    <p className="text-zinc-400 text-xs mb-2">REFUND</p>
                    <div className="flex gap-2">
                      <input data-testid="refund-amount" type="number" step="0.01" placeholder="Partial $" value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)} className="flex-1 rounded-md bg-[#141417] border border-[#27272A] px-3 py-2 text-white text-sm" />
                      <button data-testid="partial-refund-btn" disabled={!refundAmt} onClick={() => doRefund(false)} className="rounded-md border border-[#27272A] px-3 text-xs font-oswald tracking-widest hover:border-[#D4AF37] disabled:opacity-40">PARTIAL</button>
                      <button data-testid="full-refund-btn" onClick={() => doRefund(true)} className="rounded-md bg-red-600 px-3 text-xs font-oswald tracking-widest text-white">FULL REFUND</button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
